/**
 * ConsignedInventory (SCAFFOLD)
 *
 * Basic structure for the "Consigned" tab of the Inventory page. Rendered
 * inside the React app (no WCMS redirect). Modeled on the Field Inventory
 * Dashboard folder pattern.
 *
 * COMPONENT STRUCTURE:
 * └── ConsignedInventory
 *     ├── ConsignedSearch          — search-type dropdown + search box
 *     ├── ConsignedResultsHeader   — results count, current view, Export, Expand All
 *     └── ConsignedTable           — data table + pagination placeholder
 *
 * TODO(team): connect search / export / expand-all / pagination to the
 * consigned inventory API and replace hard-coded labels with i18n keys.
 */
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector,shallowEqual  } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ConsignedContainer, SearchAndResultsCard, EmptyResultsMessage } from './index.styled';
import SearchAndFilterContainer from 'src/pages/more/pages/fieldInventoryDashboard/searchAndFilterContainer';
import ConsignedResultsHeader from './ConsignedResultsHeader';
import ConsignedTable from './ConsignedTable';
import { isMobileView } from 'src/utils/util';
import {
    consignedInventoryExport,
    getConsignedInventory, 
    getConsignedPreference,
    fetchFilterData,
    resetConsignedInventoryExport,
    setConsignedSearchParams,
    setConsignedFilterData,
} from '../../redux/actions';
import { CONSIGNED_SEARCH_TYPE_OPTIONS } from './consignedInventoryConstants';
import Backdrop from 'src/common/refactoredComponents/backdrop/Backdrop';
import Spinner from 'src/common/refactoredComponents/spinner/Spinner';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
const PAGE_LIMIT = 50;
const DEFAULT_SORT_CODE = 'partnerLocName';
const DEFAULT_SORT_ORDER = 'ASC';

const MIME_TO_EXT = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'text/csv': 'csv',
    'application/csv': 'csv',
    'text/plain': 'txt',
};

const downloadBlobFile = (blob, baseFileName = 'consigned-inventory') => {
    if (!blob || typeof window === 'undefined') return;

    const mimeType = blob.type || 'application/octet-stream';
    const ext = MIME_TO_EXT[mimeType.split(';')[0].trim()] || 'xlsx';
    const fileName = `${baseFileName}.${ext}`;
    const typedBlob = blob.type ? blob : new Blob([blob], { type: mimeType });
    const url = window.URL.createObjectURL(typedBlob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const ConsignedInventory = ({
    onOpenFilters = () => {},
}) => {
    const dispatch = useDispatch();
    const {
        inventoryData = [],
        totalResults = 0,
        loading = false,
        loadingConsignedExport = false,
        exportData,
    } = useSelector(
        (state) => state.consignedInventoryReducer || {}
    );
    const { districtUID, territoryUID, accountNumber, brand,appliedFilterNames } = useSelector(
        (state) => state.consignedInventoryReducer.consignedAppliedFilters || {},
        shallowEqual
    );
    const parentLoading = useSelector(
        (state) =>
            state.loginReducer?.userDetailsLoading ||
            state.createReceiveSetsReducer?.receiveSetsLoading
    );
    const [pageNum, setPageNum] = useState(0);
    const [paginationFirst, setPaginationFirst] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [searchType, setSearchType] = useState(CONSIGNED_SEARCH_TYPE_OPTIONS[0].code);
    const [sortCode, setSortCode] = useState(DEFAULT_SORT_CODE);
    const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
    const skipNextEffect = useRef(false);

    const handleSearch = (term, type) => {
        const trimmed = term.trim();
        if (!trimmed && !searchText) return;
        setSearchText(trimmed);
        if (type) setSearchType(type);
        const activeType = type || searchType;
        dispatch(setConsignedSearchParams({ searchText: trimmed, searchType: activeType }));
        setPageNum(0);
        setPaginationFirst(0);
        skipNextEffect.current = true;
        const params = { currentPage: 0, pageSize: PAGE_LIMIT, sortCode, sortOrder };
        if (trimmed) {
            params.filterParam = activeType;
            params.searchQuery = trimmed;
        }
        params.districtUID = districtUID;
        params.territoryUID = territoryUID;
        params.accountNumber = accountNumber;
        params.brand = brand;
        dispatch(getConsignedInventory(params));
    };
    const showConsignedLoader = loading && !parentLoading;

    useEffectWithErrorLogging(() => {
        dispatch(fetchFilterData('districtFilters'));
        dispatch(fetchFilterData('territoryFilters'));
        dispatch(fetchFilterData('accountFilters'));
    }, []);

    useEffectWithErrorLogging(() => {
        if (!loadingConsignedExport && exportData) {
            const fileName = `consigned-inventory-${new Date()
                .toISOString()
                .slice(0, 10)}`;
            downloadBlobFile(
                exportData instanceof Blob ? exportData : new Blob([exportData]),
                fileName
            );
            dispatch(resetConsignedInventoryExport());
        }
    }, [dispatch, loadingConsignedExport, exportData]);

    useEffectWithErrorLogging(() => {
        if (skipNextEffect.current) {
            skipNextEffect.current = false;
            return;
        }
        const params = {
            currentPage: pageNum,
            pageSize: PAGE_LIMIT,
            sortCode,
            sortOrder,
        };
        if (searchText) {
            params.filterParam = searchType;
            params.searchQuery = searchText;
        }
        params.districtUID = districtUID;
        params.territoryUID = territoryUID;
        params.accountNumber = accountNumber;
        params.brand = brand;
        dispatch(getConsignedInventory(params));
         dispatch(fetchFilterData('consignedBrandFilters'));
    }, [dispatch, pageNum, sortCode, sortOrder]);

    const handlePageChange = (event) => {
        setPageNum(event.page);
        setPaginationFirst(event.first);
    };

    const handleSortChange = (sortParamsOrEvent) => {
        // Handle both mobile (sortParams object) and desktop (event object) patterns
        if (sortParamsOrEvent?.sortCode && sortParamsOrEvent?.sortOrder) {
            // Mobile view: direct sortCode and sortOrder
            setSortCode(sortParamsOrEvent.sortCode);
            setSortOrder(sortParamsOrEvent.sortOrder);
        } else {
            // Desktop view: DataTable event with sortField and sortOrder (1 or -1)
            setSortCode(sortParamsOrEvent?.sortField || DEFAULT_SORT_CODE);
            setSortOrder(sortParamsOrEvent?.sortOrder === -1 ? 'DESC' : 'ASC');
        }
        setPageNum(0);
        setPaginationFirst(0);
    };

    const exportToExcel = () => {
        const trimmedSearchText = searchText.trim();
        const payload = {
            sortCode,
            sortOrder,
        };
        if (trimmedSearchText) {
            payload.filterParam = searchType;
            payload.searchQuery = trimmedSearchText;
        }
        payload.districtUID = districtUID;
        payload.territoryUID = territoryUID;
        payload.accountNumber = accountNumber;
        payload.brand = brand;
        dispatch(consignedInventoryExport(payload));
    };

    const handleClearFilters = () => {
        setPageNum(0);
        setPaginationFirst(0);
        dispatch(setConsignedFilterData(null));

        const params = {
            currentPage: 0,
            pageSize: PAGE_LIMIT,
            sortCode,
            sortOrder,
        };

        if (searchText) {
            params.filterParam = searchType;
            params.searchQuery = searchText;
        }

        dispatch(getConsignedInventory(params));
    };

    const isMobile = isMobileView();

    const { t } = useTranslation();
    const getPlaceholder = (selectedType) => {
        const placeholderKey = selectedType.code === 'cfn' ? 'enterCFN' : 'enterLotSerial';
        return t(`receiveSets.InventoryConsigned.${placeholderKey}`);
    };

    return (        
        <ConsignedContainer isMobile={isMobile}>
            {!isMobile && (
                <>
                    <SearchAndFilterContainer
                        onSearch={handleSearch}
                        searchText={searchText}
                        searchType={searchType}
                        searchTypeOptions={CONSIGNED_SEARCH_TYPE_OPTIONS}
                        getPlaceholder={getPlaceholder}
                        searchLabel={t('calendar.calendarPanel.searchBox.search')}
                        searchId="consigned"
                        containerStyle={{ marginLeft: '4px', width: '100%' }}
                        marginDesktop={'8px 8px'}
                    />
                    <ConsignedResultsHeader
                        resultsCount={totalResults}
                        onExport={exportToExcel}
                        loadingExport={loadingConsignedExport}
                        consignedAppliedFilters={appliedFilterNames}
                        onClearFilters={handleClearFilters}
                        onOpenFilters={onOpenFilters}
                    />
                </>
            )}
            {isMobile && <SearchAndResultsCard>
                <SearchAndFilterContainer
                    onSearch={handleSearch}
                    searchText={searchText}
                    searchType={searchType}
                    searchTypeOptions={CONSIGNED_SEARCH_TYPE_OPTIONS}
                    getPlaceholder={getPlaceholder}
                    searchLabel={t('calendar.calendarPanel.searchBox.search')}
                    searchId="consigned"
                    searchLabelFontSize={"20px"}
                />
                <ConsignedResultsHeader
                    resultsCount={totalResults}
                    onExport={exportToExcel}
                    loadingExport={loadingConsignedExport}
                    consignedAppliedFilters={appliedFilterNames}
                    onClearFilters={handleClearFilters}
                    onOpenFilters={onOpenFilters}
                />
            </SearchAndResultsCard>}
            {showConsignedLoader && (
                <Backdrop
                    showCloseButton={false}
                    showBackdrop={true}
                    isModal
                    loaderBackground
                >
                    <Spinner
                        id="consigned-inventory-loading-spinner"
                        animation="border"
                        variant="primary"
                        small
                    />
                </Backdrop>
            )}
            {!loading && totalResults === 0 && (
                <EmptyResultsMessage isMobile={isMobile}>
                    {t(`receiveSets.setsInventory.noResultsMessage`)}
                </EmptyResultsMessage>
            )}
            {totalResults > 0 && (
                <ConsignedTable
                    rows={inventoryData}
                    totalRecords={totalResults}
                    recordsPerPage={PAGE_LIMIT}
                    pageNumber={pageNum}
                    first={paginationFirst}
                    onPageChange={handlePageChange}
                    onSortChange={handleSortChange}
                    sortField={sortCode}
                    sortOrder={sortOrder === 'ASC' ? 1 : -1}
                />
            )}
        </ConsignedContainer>
    );
};

export default ConsignedInventory;
